import React, { useEffect, useState } from 'react';
import {
    Camera, Check, CalendarDays, Clock3, Heart, Link2, Mail, MapPin,
    PenLine, Phone, Play, Plus, Power, PowerOff, Sparkles, Trash2,
    UserRound, Video, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SafeImage from '../../components/SafeImage';
import { onAdminRefresh } from '../../utils/adminSocket';
import ConfirmDialog from '../../components/ConfirmDialog';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { RULES, checkFiles, limitNote, uploadFailMessage } from '../../utils/uploadRules';
import './Testimonials.css';

const IMG_BASE = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
const PAGE_SIZE = 10;

const emptyStoryForm = { groomName: '', brideName: '', marriageDate: '', message: '', city: '', religion: 'Hindu' };
const emptyVideoForm = { groomName: '', brideName: '', videoUrl: '' };

// Extracts the video id from a watch/share/short/embed URL, so admin doesn't
// need to separately upload a thumbnail for video stories, and so the same
// id can drive an inline embedded player.
const youtubeId = (url) => {
    const match = String(url || '').match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{11})/);
    return match ? match[1] : '';
};
const youtubeThumb = (url) => {
    const id = youtubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
};

const Testimonials = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('stories');
    const [showForm, setShowForm] = useState(false);
    const [showVideoForm, setShowVideoForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // { type: 'delete' | 'reject', id, label }
    const [form, setForm] = useState(emptyStoryForm);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState('');
    const [savedPhotoPreview, setSavedPhotoPreview] = useState(''); // the photo already stored on the server (when editing)
    const [videoForm, setVideoForm] = useState(emptyVideoForm);
    const [editingId, setEditingId] = useState(null);
    const [playingId, setPlayingId] = useState(null);
    const [counts, setCounts] = useState({ stories: 0, videos: 0, pending: 0 });
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => { fetchTestimonials(1); }, [tab]);

    // realtime: a story submitted on the website (or approved / rejected elsewhere) refreshes the counts and the list at once
    useEffect(() => onAdminRefresh(() => fetchTestimonials(1, false, true)), [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchTestimonials = async (targetPage = 1, append = false, silent = false) => {
        if (append) setLoadingMore(true);
        else if (!silent) setLoading(true);
        try {
            const res = await API.get('/testimonials/admin/all', { params: { tab, limit: PAGE_SIZE, page: targetPage } });
            setTestimonials(current => append ? [...current, ...(res.data.testimonials || [])] : (res.data.testimonials || []));
            setCounts(res.data.counts || { stories: 0, videos: 0, pending: 0 });
            setTotal(res.data.total || 0);
            setPage(res.data.page || targetPage);
            setHasMore(Boolean(res.data.hasMore));
        } catch (err) {
            toast.error('Failed to load');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const stories = testimonials.filter(t => (t.type || 'story') === 'story' && (t.status || 'approved') === 'approved');
    const videos = testimonials.filter(t => t.type === 'video');
    const pending = testimonials.filter(t => t.status === 'pending');

    const visibleStories = stories;
    const visibleVideos = videos;

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!checkFiles([file], RULES.story).length) { e.target.value = ''; return; }
        setPhoto(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    // Takes back a photo that was just picked (the stored photo, if there is one, comes back)
    const removeNewPhoto = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPhoto(null);
        setPhotoPreview(savedPhotoPreview);
    };

    const resetForm = () => {
        setForm(emptyStoryForm);
        setPhoto(null);
        setPhotoPreview('');
        setSavedPhotoPreview('');
        setEditingId(null);
    };

    const startEditStory = (t) => {
        setForm({
            groomName: t.groomName || '',
            brideName: t.brideName || '',
            marriageDate: t.marriageDate ? t.marriageDate.slice(0, 10) : '',
            message: t.message || '',
            city: t.city || '',
            religion: t.religion || 'Hindu',
        });
        setPhoto(null);
        setPhotoPreview(t.couplePhoto ? `${IMG_BASE}${t.couplePhoto}` : '');
        setSavedPhotoPreview(t.couplePhoto ? `${IMG_BASE}${t.couplePhoto}` : '');
        setEditingId(t._id);
        setShowForm(true);
    };

    const startEditVideo = (v) => {
        setVideoForm({ groomName: v.groomName || '', brideName: v.brideName || '', videoUrl: v.videoUrl || '' });
        setEditingId(v._id);
        setShowVideoForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const data = new FormData();
            Object.entries(form).forEach(([k, v]) => data.append(k, v));
            if (photo) data.append('couplePhoto', photo);
            if (editingId) {
                await API.put(`/testimonials/${editingId}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
                toast.success('Success story updated!');
            } else {
                await API.post('/testimonials', data, { headers: { 'Content-Type': 'multipart/form-data' } });
                toast.success('Success story added!');
            }
            setShowForm(false);
            resetForm();
            fetchTestimonials();
        } catch (err) {
            toast.error(photo && (err.response?.status === 413 || !err.response) ? uploadFailMessage(err, [photo], RULES.story) : (err.response?.data?.message || 'Could not save the success story. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleVideoSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingId) {
                await API.put(`/testimonials/${editingId}`, videoForm);
                toast.success('Video story updated!');
            } else {
                await API.post('/testimonials/video', videoForm);
                toast.success('Video story added!');
            }
            setShowVideoForm(false);
            setVideoForm(emptyVideoForm);
            setEditingId(null);
            fetchTestimonials();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed!');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (t) => {
        try {
            const res = await API.put(`/testimonials/${t._id}/toggle-active`);
            toast.success(res.data.message);
            fetchTestimonials();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update story');
        }
    };

    const handleApprove = async (t) => {
        try {
            await API.put(`/testimonials/${t._id}/approve`);
            toast.success(`${t.groomName} & ${t.brideName}'s story is now live`);
            fetchTestimonials();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to approve story');
        }
    };

    const handleReject = (t) => setConfirmAction({ type: 'reject', id: t._id, label: `${t.groomName} & ${t.brideName}` });
    const handleDelete = (t) => setConfirmAction({ type: 'delete', id: t._id, label: `${t.groomName} & ${t.brideName}` });

    const runConfirmAction = async () => {
        const action = confirmAction;
        if (!action) return;
        setConfirmAction(null);
        try {
            if (action.type === 'reject') {
                await API.put(`/testimonials/${action.id}/reject`);
                toast.success('Story rejected');
            } else {
                await API.delete(`/testimonials/${action.id}`);
                toast.success('Deleted!');
            }
            fetchTestimonials();
        } catch (err) {
            toast.error(err.response?.data?.message || (action.type === 'reject' ? 'Failed to reject story' : 'Failed to delete story'));
        }
    };

    const TABS = [
        { id: 'stories', label: 'Featured Stories', Icon: Heart, count: counts.stories },
        { id: 'videos', label: 'Video Stories', Icon: Video, count: counts.videos },
        { id: 'pending', label: 'Pending Submissions', Icon: Clock3, count: counts.pending },
    ];

    const stats = [
        { label: 'Featured stories', value: counts.stories, Icon: Heart, tone: 'primary' },
        { label: 'Video stories', value: counts.videos, Icon: Video, tone: 'violet' },
        { label: 'Pending review', value: counts.pending, Icon: Clock3, tone: 'amber' },
    ];

    return (
        <div className="testimonials-layout">
            <Sidebar />
            <main className="admin-content testimonials-content">
                <Navbar title={<span className="testimonials-title"><Heart size={19} /> Success Stories</span>} />
                <div className="testimonials-page">
                    <div className="testimonials-heading">
                        <div>
                            <h2>Success stories</h2>
                            <p>Manage featured stories, video stories, and public submissions.</p>
                        </div>
                    </div>

                    <section className="testimonials-stats" aria-label="Success story overview">
                        {stats.map(({ label, value, Icon, tone }) => (
                            <article className="testimonials-stat-card" key={label}>
                                <span className={`testimonials-stat-icon ${tone}`}><Icon size={19} /></span>
                                <div><strong>{value}</strong><span>{label}</span></div>
                            </article>
                        ))}
                    </section>

                    <section className="testimonials-panel">
                        <div className="testimonials-toolbar">
                            <div className="testimonials-tabs" aria-label="Switch story type">
                                {TABS.map(({ id, label, Icon, count }) => (
                                    <button key={id} type="button" className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
                                        <Icon size={14} />{label}{count > 0 && <span className={id === 'pending' ? 'is-alert' : ''}>{count}</span>}
                                    </button>
                                ))}
                            </div>
                            {tab === 'stories' && (
                                <button type="button" className="testimonials-add-btn" onClick={() => { if (showForm) resetForm(); setShowForm(v => !v); }}>
                                    {showForm ? <X size={15} /> : <Plus size={15} />}{showForm ? 'Cancel' : 'Add Success Story'}
                                </button>
                            )}
                            {tab === 'videos' && (
                                <button type="button" className="testimonials-add-btn" onClick={() => { if (showVideoForm) { setVideoForm(emptyVideoForm); setEditingId(null); } setShowVideoForm(v => !v); }}>
                                    {showVideoForm ? <X size={15} /> : <Plus size={15} />}{showVideoForm ? 'Cancel' : 'Add Video Story'}
                                </button>
                            )}
                        </div>

                        {tab === 'stories' && showForm && (
                            <form className="testimonials-form" onSubmit={handleSubmit}>
                                <h3><PenLine size={15} /> {editingId ? 'Edit Success Story' : 'Add New Success Story'}</h3>
                                <div className="testimonials-form-body">
                                    <label className="testimonials-photo-upload">
                                        {photoPreview ? (
                                            <SafeImage src={photoPreview} alt="Preview" />
                                        ) : (
                                            <div className="testimonials-photo-placeholder">
                                                <Camera size={24} />
                                                <span>Click to upload photo</span>
                                                <small className="upload-limit"><b>Limit:</b> {limitNote(RULES.story)}</small>
                                            </div>
                                        )}
                                        <input type="file" accept="image/*" onChange={handlePhotoChange} />
                                        {photo && (
                                            <button type="button" className="testimonials-photo-remove" onClick={removeNewPhoto} aria-label="Remove the selected photo" title="Remove">
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </label>
                                    <div className="testimonials-form-fields">
                                        <div className="testimonials-form-grid">
                                            <label className="testimonials-field">
                                                <span>Groom Name</span>
                                                <input placeholder="Groom's name" value={form.groomName} onChange={e => setForm({ ...form, groomName: e.target.value })} required />
                                            </label>
                                            <label className="testimonials-field">
                                                <span>Bride Name</span>
                                                <input placeholder="Bride's name" value={form.brideName} onChange={e => setForm({ ...form, brideName: e.target.value })} required />
                                            </label>
                                            <label className="testimonials-field">
                                                <span>Marriage Date</span>
                                                <DatePicker value={form.marriageDate} onChange={v => setForm({ ...form, marriageDate: v })} />
                                            </label>
                                            <label className="testimonials-field">
                                                <span>City</span>
                                                <input placeholder="City" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
                                            </label>
                                            <label className="testimonials-field">
                                                <span>Religion</span>
                                                <Select
                                                    value={form.religion}
                                                    onChange={v => setForm({ ...form, religion: v })}
                                                    options={['Hindu', 'Muslim', 'Christian']}
                                                    allowEmpty={false}
                                                    ariaLabel="Religion"
                                                />
                                            </label>
                                        </div>
                                        <label className="testimonials-field">
                                            <span>Their Message</span>
                                            <textarea placeholder="Their success story message..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
                                        </label>
                                    </div>
                                </div>
                                <button type="submit" className="testimonials-submit-btn" disabled={saving}>
                                    <Heart size={15} /> {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Success Story'}
                                </button>
                            </form>
                        )}

                        {tab === 'videos' && showVideoForm && (
                            <form className="testimonials-form" onSubmit={handleVideoSubmit}>
                                <h3><PenLine size={15} /> {editingId ? 'Edit Video Story' : 'Add New Video Story'}</h3>
                                <div className="testimonials-form-grid">
                                    <label className="testimonials-field">
                                        <span>Groom Name</span>
                                        <input placeholder="Groom's name" value={videoForm.groomName} onChange={e => setVideoForm({ ...videoForm, groomName: e.target.value })} required />
                                    </label>
                                    <label className="testimonials-field">
                                        <span>Bride Name</span>
                                        <input placeholder="Bride's name" value={videoForm.brideName} onChange={e => setVideoForm({ ...videoForm, brideName: e.target.value })} required />
                                    </label>
                                    <label className="testimonials-field testimonials-field-wide">
                                        <span>YouTube Video URL</span>
                                        <input placeholder="https://www.youtube.com/watch?v=..." value={videoForm.videoUrl} onChange={e => setVideoForm({ ...videoForm, videoUrl: e.target.value })} required />
                                    </label>
                                </div>
                                <button type="submit" className="testimonials-submit-btn" disabled={saving}>
                                    <Video size={15} /> {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Video Story'}
                                </button>
                            </form>
                        )}

                        {loading ? (
                            <div className="testimonials-state"><span className="testimonials-spinner" />Loading...</div>
                        ) : (
                            <div className="testimonials-grid">
                                {tab === 'stories' && visibleStories.map(t => (
                                    <article className={`testimonials-card ${t.isActive === false ? 'is-hidden' : ''}`} key={t._id}>
                                        <div className="testimonials-card-head">
                                            {t.couplePhoto ? (
                                                <SafeImage src={`${IMG_BASE}${t.couplePhoto}`} alt={`${t.groomName} & ${t.brideName}`} className="testimonials-couple-photo" />
                                            ) : (
                                                <div className="testimonials-couple-fallback"><Heart size={24} /></div>
                                            )}
                                            <div className="testimonials-card-actions">
                                                <button type="button" onClick={() => startEditStory(t)} title="Edit story" aria-label="Edit story"><PenLine size={14} /></button>
                                                <button type="button" onClick={() => handleToggleActive(t)} title={t.isActive === false ? 'Show on website' : 'Hide from website'} aria-label={t.isActive === false ? 'Show on website' : 'Hide from website'}>
                                                    {t.isActive === false ? <Power size={14} /> : <PowerOff size={14} />}
                                                </button>
                                                <button type="button" className="danger" onClick={() => handleDelete(t)} title="Delete story" aria-label="Delete story"><Trash2 size={14} /></button>
                                            </div>
                                        </div>
                                        <div className="testimonials-couple-names">{t.groomName} &amp; {t.brideName}</div>
                                        <div className="testimonials-meta-row">
                                            {t.religion && <span>{t.religion}</span>}
                                            {t.city && <span><MapPin size={11} />{t.city}</span>}
                                            {t.marriageDate && <span><CalendarDays size={11} />{new Date(t.marriageDate).toLocaleDateString('en-IN')}</span>}
                                        </div>
                                        {t.isActive === false && <span className="testimonials-hidden-tag">Hidden from website</span>}
                                        {t.message && <p className="testimonials-message"><Sparkles size={12} />&nbsp;{t.message}</p>}
                                    </article>
                                ))}

                                {tab === 'videos' && visibleVideos.map(v => {
                                    const id = youtubeId(v.videoUrl);
                                    const isPlaying = playingId === v._id;
                                    return (
                                    <article className="testimonials-card testimonials-video-card" key={v._id}>
                                        <div className="testimonials-video-thumb">
                                            {isPlaying ? (
                                                <>
                                                    <iframe
                                                        src={`https://www.youtube.com/embed/${id}?autoplay=1`}
                                                        title={`${v.groomName} & ${v.brideName}`}
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                    <button type="button" className="testimonials-video-close" onClick={() => setPlayingId(null)} aria-label="Collapse video">
                                                        <X size={14} />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="testimonials-video-play-trigger"
                                                    onClick={() => id && setPlayingId(v._id)}
                                                    aria-label={`Play ${v.groomName} & ${v.brideName}'s video`}
                                                >
                                                    {youtubeThumb(v.videoUrl) ? (
                                                        <img src={youtubeThumb(v.videoUrl)} alt={`${v.groomName} & ${v.brideName}`} />
                                                    ) : (
                                                        <div className="testimonials-couple-fallback"><Video size={24} /></div>
                                                    )}
                                                    <span className="testimonials-play"><Play size={20} /></span>
                                                </button>
                                            )}
                                        </div>
                                        <div className="testimonials-card-head" style={{ marginTop: 10 }}>
                                            <div className="testimonials-couple-names">{v.groomName} &amp; {v.brideName}</div>
                                            <div className="testimonials-card-actions">
                                                <button type="button" onClick={() => startEditVideo(v)} title="Edit video" aria-label="Edit video"><PenLine size={14} /></button>
                                                <a href={v.videoUrl} target="_blank" rel="noreferrer" title="Open on YouTube" aria-label="Open on YouTube"><Link2 size={14} /></a>
                                                <button type="button" className="danger" onClick={() => handleDelete(v)} title="Delete video" aria-label="Delete video"><Trash2 size={14} /></button>
                                            </div>
                                        </div>
                                    </article>
                                    );
                                })}

                                {tab === 'pending' && pending.map(p => (
                                    <article className="testimonials-card" key={p._id}>
                                        <div className="testimonials-card-head">
                                            {p.couplePhoto ? (
                                                <SafeImage src={`${IMG_BASE}${p.couplePhoto}`} alt={`${p.groomName} & ${p.brideName}`} className="testimonials-couple-photo" />
                                            ) : (
                                                <div className="testimonials-couple-fallback"><UserRound size={24} /></div>
                                            )}
                                            <span className="testimonials-badge pending"><Clock3 size={11} />Pending</span>
                                        </div>
                                        <div className="testimonials-couple-names">{p.groomName} &amp; {p.brideName}</div>
                                        <div className="testimonials-meta-row">
                                            {p.submitterEmail && <span><Mail size={11} />{p.submitterEmail}</span>}
                                            {p.submitterPhone && <span><Phone size={11} />{p.submitterPhone}</span>}
                                        </div>
                                        {p.message && <p className="testimonials-message"><Sparkles size={12} />&nbsp;{p.message}</p>}
                                        <div className="testimonials-pending-actions">
                                            <button type="button" className="approve" onClick={() => handleApprove(p)}><Check size={14} />Approve</button>
                                            <button type="button" className="reject" onClick={() => handleReject(p)}><X size={14} />Reject</button>
                                        </div>
                                    </article>
                                ))}

                                {tab === 'stories' && stories.length === 0 && (
                                    <div className="testimonials-empty"><Heart size={28} /><strong>No success stories yet</strong><span>Add one, or wait for a public submission to be approved.</span></div>
                                )}
                                {tab === 'videos' && videos.length === 0 && (
                                    <div className="testimonials-empty"><Video size={28} /><strong>No video stories yet</strong><span>Add a YouTube link to feature a couple's video story.</span></div>
                                )}
                                {tab === 'pending' && pending.length === 0 && (
                                    <div className="testimonials-empty"><Clock3 size={28} /><strong>No pending submissions</strong><span>Stories submitted from the website via "Tell Us Your Story" will show up here.</span></div>
                                )}
                            </div>
                        )}

                        {hasMore && (
                            <div className="testimonials-load-more">
                                <button type="button" disabled={loadingMore} onClick={() => fetchTestimonials(page + 1, true)}>
                                    {loadingMore ? 'Loading...' : `Load 10 more (${Math.max(total - testimonials.length, 0)} remaining)`}
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            </main>

            <ConfirmDialog
                open={Boolean(confirmAction)}
                title={confirmAction?.type === 'reject' ? 'Reject Story?' : 'Delete Story?'}
                message={confirmAction ? `${confirmAction.type === 'reject' ? 'Reject' : 'Permanently delete'} ${confirmAction.label}'s story?` : ''}
                confirmLabel={confirmAction?.type === 'reject' ? 'Reject' : 'Delete Story'}
                danger
                onConfirm={runConfirmAction}
                onCancel={() => setConfirmAction(null)}
            />
        </div>
    );
};

export default Testimonials;
